import type { ErrorEvent, EventHint, Exception, StackFrame } from '@sentry/react';
import { shouldDropNetworkFetchEvent } from './sentry-network-filter';

const METAMASK_EXTENSION_ID = 'nkbihfbeogaeaoehlefnkodbefgpgknn';
const METAMASK_EXTENSION_URL_PREFIX = `chrome-extension://${METAMASK_EXTENSION_ID}/`;
const METAMASK_NOISE_MESSAGES = [
  'Failed to connect to MetaMask',
  'MetaMask extension not found',
] as const;

const exceptionValue = (exception: Exception): string =>
  typeof exception.value === 'string' ? exception.value : '';

const frameFilename = (frame: StackFrame): string =>
  typeof frame.filename === 'string'
    ? frame.filename
    : typeof frame.abs_path === 'string'
      ? frame.abs_path
      : '';

const exceptionHasMetaMaskFrame = (exception: Exception): boolean =>
  exception.stacktrace?.frames?.some((frame) =>
    frameFilename(frame).startsWith(METAMASK_EXTENSION_URL_PREFIX)
  ) ?? false;

const exceptionHasMetaMaskMessage = (exception: Exception): boolean =>
  METAMASK_NOISE_MESSAGES.some((message) => exceptionValue(exception).includes(message));

const originalExceptionHasMetaMaskStack = (hint?: EventHint): boolean => {
  const originalException = hint?.originalException;
  return (
    originalException instanceof Error &&
    METAMASK_NOISE_MESSAGES.some((message) => originalException.message.includes(message)) &&
    typeof originalException.stack === 'string' &&
    originalException.stack.includes(METAMASK_EXTENSION_URL_PREFIX)
  );
};

export const isMetaMaskExtensionNoiseEvent = (
  event: ErrorEvent,
  hint?: EventHint
): boolean => {
  const exceptions = event.exception?.values ?? [];
  if (exceptions.length === 0) return false;

  const hasMetaMaskMessage = exceptions.some(exceptionHasMetaMaskMessage);
  if (!hasMetaMaskMessage) return false;

  return exceptions.some(exceptionHasMetaMaskFrame) || originalExceptionHasMetaMaskStack(hint);
};

export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  if (shouldDropNetworkFetchEvent(event)) return null;
  if (isMetaMaskExtensionNoiseEvent(event, hint)) return null;
  return event;
};
